import { useBufferedFilteredEntries } from 'features/drilldown/rolodex/dataHelpers/useFilteredEntries'
import {
  EnrichedOntologyFieldDefinition,
  extractValueFromEntry,
  useOntology,
} from 'features/dynamicOntology'
import { forecastMath } from 'features/math'
import { useMemo } from 'react'
import useSWR from 'swr'
import { GeckoEntry, RolodexHistogramFilter } from 'types'

import {
  DrilldownMetricConfig,
  ExpressionType,
  FIELD_OPERATOR_META,
  FieldExpression,
  MetricCalculationType,
} from './MetricsPanelConfig'

// Result type for metric calculations
interface MetricResult {
  value: number
  isLoading: boolean
  error?: string
}

// Helper type for calculation functions
type CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
) => number

const calculateCount: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>
) => {
  return data.length
}

const calculateSum: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
) => {
  const getValue = makeEntryValueGetter(config, ontologyFields ?? {})
  return data.reduce((sum, entry) => sum + getValue(entry), 0)
}

const calculateMin: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
) => {
  if (data.length === 0) return 0

  const getValue = makeEntryValueGetter(config, ontologyFields ?? {})
  return Math.min(...data.map(getValue))
}

const calculateMax: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
) => {
  if (data.length === 0) return 0

  const getValue = makeEntryValueGetter(config, ontologyFields ?? {})
  return Math.max(...data.map(getValue))
}

const calculateMean: CalculationFunction = (
  data: ReadonlyArray<GeckoEntry>,
  config: DrilldownMetricConfig,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
) => {
  if (data.length === 0) return 0

  return calculateSum(data, config, filters, ontologyFields) / data.length
}

// Calculation function lookup table
const calculators: Record<MetricCalculationType, CalculationFunction> = {
  [MetricCalculationType.COUNT]: calculateCount,
  [MetricCalculationType.SUM]: calculateSum,
  [MetricCalculationType.MIN]: calculateMin,
  [MetricCalculationType.MAX]: calculateMax,
  [MetricCalculationType.MEAN]: calculateMean,
}

/**
 * Main metric calculation service
 * Routes to specific calculation functions based on the metric configuration
 */
export async function calculateMetric(
  config: DrilldownMetricConfig,
  data: ReadonlyArray<GeckoEntry>,
  filters?: ReadonlyArray<RolodexHistogramFilter>,
  ontologyFields?: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
): Promise<MetricResult> {
  try {
    const calculator: CalculationFunction | undefined =
      calculators[config.calculationType]
    if (!calculator) {
      throw new Error(`Unsupported calculation type: ${config.calculationType}`)
    }

    const rawValue = calculator(data, config, filters, ontologyFields)
    const value =
      config.numDecimals !== undefined
        ? Number(rawValue.toFixed(config.numDecimals))
        : rawValue

    return {
      value,
      isLoading: false,
    }
  } catch (error) {
    return {
      value: 0,
      isLoading: false,
      error: error instanceof Error ? error.message : 'Calculation error',
    }
  }
}

// - - - - - - - - - - - CALCULATION FUNCTIONS - - - - - - - - - - -

/**
 * Converts a FieldExpression tree into a mathjs expression string, e.g.
 * `(field0 - field1) * (field2 - field3)`. Each FIELD node is assigned a
 * variable name, recorded in `fieldsByVariable` so values can be substituted
 * into the evaluation scope per entry. (Field keys themselves contain dots,
 * so they can't be used as mathjs identifiers directly.)
 */
function toMathExpression(
  expressions: ReadonlyArray<FieldExpression>,
  fieldsByVariable: Map<string, string>
): string {
  return expressions
    .map((expr) => {
      switch (expr.type) {
        case ExpressionType.FIELD: {
          const variable = `field${fieldsByVariable.size}`
          fieldsByVariable.set(variable, expr.field)
          return variable
        }
        case ExpressionType.GROUP:
          return `(${toMathExpression(expr.expressions, fieldsByVariable)})`
        case ExpressionType.OPERATION:
          return FIELD_OPERATOR_META[expr.operation].mathSymbol
      }
    })
    .join(' ')
}

/**
 * Compiles the config's field expressions once and returns a function that
 * evaluates them against a single entry.
 */
function makeEntryValueGetter(
  config: DrilldownMetricConfig,
  ontologyFields: Readonly<Record<string, EnrichedOntologyFieldDefinition>>
): (entry: GeckoEntry) => number {
  if (!config.fieldExpressions || config.fieldExpressions.length === 0) {
    throw new Error('Field expressions are required for this calculation type')
  }

  const fieldsByVariable = new Map<string, string>()
  const compiled = forecastMath
    .parse(toMathExpression(config.fieldExpressions, fieldsByVariable))
    .compile()

  const fieldDefsByVariable = Array.from(fieldsByVariable).map(
    ([variable, field]): [string, EnrichedOntologyFieldDefinition] => {
      const fieldDef = ontologyFields[field]
      if (!fieldDef) {
        throw new Error(`Field '${field}' not found in ontology`)
      }
      return [variable, fieldDef]
    }
  )

  return (entry: GeckoEntry) => {
    const scope = Object.fromEntries(
      fieldDefsByVariable.map(([variable, fieldDef]) => {
        const value = extractValueFromEntry(entry, fieldDef)
        return [variable, typeof value === 'number' ? value : 0]
      })
    )
    const result = compiled.evaluate(scope)
    // Guard against division by zero etc. producing Infinity/NaN
    return typeof result === 'number' && Number.isFinite(result) ? result : 0
  }
}

// - - - - - - - - - - - HELPER FUNCTIONS - - - - - - - - - - -
/**
 * React hook to calculate a metric value
 * Integrates with the drilldown data system and provides loading states
 */
export function useMetricCalculation(
  config: DrilldownMetricConfig
): MetricResult {
  const { data: entries, isLoading: isLoadingData } =
    useBufferedFilteredEntries({
      entryType: config.rolodexType,
      dataKey: 'entries',
      limit: undefined,
    })
  const { fields } = useOntology()

  const filteredEntries = useMemo(
    () => entries.filter((entry) => entry.type === config.rolodexType) ?? [],
    [entries, config.rolodexType]
  )

  const {
    data: result,
    isLoading: isCalculating,
    error,
  } = useSWR(
    // The full config is part of the key (SWR hashes it stably) so that
    // editing a metric's expressions recalculates instead of hitting a
    // stale cache entry for the same metricType.
    !isLoadingData && filteredEntries.length > 0
      ? ['metric-calculation', config, filteredEntries.length]
      : null,
    () => calculateMetric(config, filteredEntries, undefined, fields),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000, // Cache for 30 seconds
    }
  )

  return {
    value: result?.value ?? 0,
    isLoading: isLoadingData || isCalculating,
    error: error?.message || result?.error,
  }
}
